/* eslint-disable max-params */
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';

import {
  getAllRoadmapNodes,
  getInitiativeById,
  getRoadmapsDropdownVisible,
  getRoadmapsPopupVisible,
  getRoadmapsRoadmapVisible,
  importNewRoadmapInitiativeNode,
} from '@/features/canvas/api';
import { getBudgetDropdownsVisible } from '@/features/canvas/api/budget-dropdown-visible';
import { getBudgetPopupsVisible } from '@/features/canvas/api/budget-popup-visible';
import { getBudgetRoadmapVisible } from '@/features/canvas/api/budget-roadmap-visible';
import { getAllBudgetsForRoadmap } from '@/features/canvas/api/initiative_budgets';
import { getInitiativeChildren } from '@/features/canvas/api/initiative-relationships';
import { FilteredNodes, useEditorContext } from '@/features/canvas/contexts/editor-context';
import { useImportInitiativeContext } from '@/features/canvas/contexts/import-initiative-context';
import { useCSVImportedInitiativeDrop } from '@/features/canvas/hooks/useCSVImportedInitiative';
import { FilterOptionValue } from '@/features/canvas/types/filter-option';
import { InitiativeFieldValues, InitiativeToBeImported } from '@/features/canvas/types/initiative';
import { useShowToast } from '@/hooks/useShowToast';

interface UseImportedInitiativeDropProps {
  roadmapId: number;
}

export const useImportedInitiativeDrop = ({ roadmapId }: UseImportedInitiativeDropProps) => {
  const { t } = useTranslation();
  const {
    updateLastSavedTime,
    filteredNodes,
    setFilteredNodes,
    generateUniqueIntTemporaryId,
    setSelectedNodeId,
    temporaryCreatedIds,
    roadmapNodes,
  } = useEditorContext();
  const { showToast } = useShowToast();
  const queryClient = useQueryClient();

  const { setIsInitiativeImportRunning, selectedRoadmapId } = useImportInitiativeContext();

  const { mutateAsync: importNewRoadmapInitiativeNodeMutation } = useMutation(
    importNewRoadmapInitiativeNode
  );

  const { onCSVImportedInitiativeDrop } = useCSVImportedInitiativeDrop({ roadmapId });

  // Recursive function to create nodes for all descendants
  const createNodesForDescendants = useCallback(
    async (
      currentInitiativeId: number,
      baseX: number,
      baseY: number,
      sectionId: number,
      timePeriodId: number,
      foreignRoadmapId: number,
      depth = 0
    ) => {
      try {
        const descendants = await getInitiativeChildren({ initiativeId: currentInitiativeId });

        // Filter descendants that need nodes created
        const descendantsNeedingNodes = descendants.filter(descendant => {
          const hasNodeInCurrentRoadmap = roadmapNodes?.some(
            node => node.initiatives?.id === descendant.id
          );
          return !hasNodeInCurrentRoadmap && descendant.id;
        });

        // Create all nodes for descendants concurrently
        const nodeCreationPromises = descendantsNeedingNodes.map(async (descendant, index) => {
          // Calculate position with offset from parent
          const offsetX = baseX + (depth + 1) * 60; // Spread horizontally by depth
          const offsetY = baseY + index * 40; // Spread vertically by child index

          // Create node for this descendant
          await importNewRoadmapInitiativeNodeMutation({
            roadmapId,
            initiativeId: descendant.id!,
            nodeInfo: {
              x: offsetX,
              y: offsetY,
              sectionId,
              timePeriodId,
              foreignRoadmapId,
            },
          });

          return { descendant, offsetX, offsetY };
        });

        // Wait for all nodes to be created
        const createdNodes = await Promise.all(nodeCreationPromises);

        // Recursively create nodes for each descendant's children
        const recursionPromises = createdNodes.map(({ descendant, offsetX, offsetY }) =>
          createNodesForDescendants(
            descendant.id!,
            offsetX,
            offsetY,
            sectionId,
            timePeriodId,
            foreignRoadmapId,
            depth + 1
          )
        );
        await Promise.all(recursionPromises);
      } catch (error) {
        // If we can't fetch children, skip this branch but don't fail the whole operation
      }
    },
    [roadmapNodes, importNewRoadmapInitiativeNodeMutation, roadmapId]
  );

  const onImportInitiativeDrop = useCallback(
    async ({
      initiative,
      newNodesCanvasXPercentage,
      newNodesCanvasYPercentage,
      sectionId,
      timePeriodId,
      newInitiativeFieldValues,
      newInitiativeFilterValues,
    }: {
      initiative: InitiativeToBeImported;
      newNodesCanvasXPercentage: number;
      newNodesCanvasYPercentage: number;
      sectionId: number;
      timePeriodId: number;
      newInitiativeFieldValues?: InitiativeFieldValues[];
      newInitiativeFilterValues?: FilterOptionValue[];
    }) => {
      setIsInitiativeImportRunning(true);
      try {
        const newNodeTempId = generateUniqueIntTemporaryId();
        const newInitiativeTempId = generateUniqueIntTemporaryId();

        const newRoadmapNode = {
          id: newNodeTempId,
          title: initiative?.title,
          isVisible: true,
          x: newNodesCanvasXPercentage,
          y: newNodesCanvasYPercentage,
          timePeriodId,
          sectionId,
          initiatives: {
            title: initiative?.title,
          },
        };

        const newNodesQueryData = queryClient.setQueryData(
          [getAllRoadmapNodes.name, roadmapId],
          (oldNodes?: FilteredNodes[]) => {
            const newRoadmapNodeWithInitiative = {
              ...{ ...newRoadmapNode, shape: 'circle' as const, color: '' },
              initiatives: {
                id: newInitiativeTempId,
                title: initiative?.title,
              },
            };
            if (oldNodes) {
              const newNodes: FilteredNodes[] = [...oldNodes, newRoadmapNodeWithInitiative];
              setFilteredNodes(newNodes);
              return newNodes;
            }

            setFilteredNodes([newRoadmapNodeWithInitiative] as unknown as typeof filteredNodes);
            return [newRoadmapNodeWithInitiative];
          }
        );

        if (initiative.isImportedFromCSV) {
          await onCSVImportedInitiativeDrop({
            initiative,
            newRoadmapNode,
            newInitiativeFieldValues,
            newInitiativeFilterValues,
          });
        } else {
          const newlyCreatedNode = await importNewRoadmapInitiativeNodeMutation({
            roadmapId,
            initiativeId: initiative.id!,
            nodeInfo: {
              x: newNodesCanvasXPercentage,
              y: newNodesCanvasYPercentage,
              sectionId,
              timePeriodId,
              foreignRoadmapId: selectedRoadmapId!,
            },
          });

          queryClient.setQueryData(
            [getAllRoadmapNodes.name, roadmapId],
            newNodesQueryData.map(n => {
              if (n.id === newNodeTempId) {
                temporaryCreatedIds.delete(newNodeTempId);
                temporaryCreatedIds.delete(newInitiativeTempId);
                return {
                  ...newlyCreatedNode,
                };
              }
              return n;
            })
          );

          // Create nodes for all descendants recursively
          await createNodesForDescendants(
            newlyCreatedNode.initiatives.id!,
            newNodesCanvasXPercentage,
            newNodesCanvasYPercentage,
            sectionId,
            timePeriodId,
            selectedRoadmapId!,
            0
          );

          await Promise.all([
            queryClient.invalidateQueries([
              getInitiativeById.name,
              newlyCreatedNode.initiatives.id,
            ]),
            queryClient.invalidateQueries([getRoadmapsDropdownVisible.name, selectedRoadmapId]),
            queryClient.invalidateQueries([getRoadmapsRoadmapVisible.name, selectedRoadmapId]),
            queryClient.invalidateQueries([getRoadmapsPopupVisible.name, selectedRoadmapId]),
          ]);
          updateLastSavedTime();
          setSelectedNodeId(newlyCreatedNode.id!);
        }

        await Promise.all([
          queryClient.invalidateQueries([getAllRoadmapNodes.name, roadmapId]),
          queryClient.invalidateQueries([getRoadmapsDropdownVisible.name, roadmapId]),
          queryClient.invalidateQueries([getRoadmapsRoadmapVisible.name, roadmapId]),
          queryClient.invalidateQueries([getRoadmapsPopupVisible.name, roadmapId]),
          queryClient.invalidateQueries([getAllBudgetsForRoadmap.name, roadmapId]),
          queryClient.invalidateQueries(['roadmap-budget-summary', roadmapId]),
          queryClient.invalidateQueries(['budget-by-time-period', roadmapId]),
          queryClient.invalidateQueries(['budget-by-section', roadmapId]),
          // Invalidate budget visibility queries to ensure imported initiatives show budget fields
          queryClient.invalidateQueries([getBudgetPopupsVisible.name, Number(roadmapId)]),
          queryClient.invalidateQueries([getBudgetRoadmapVisible.name, Number(roadmapId)]),
          queryClient.invalidateQueries([getBudgetDropdownsVisible.name, Number(roadmapId)]),
        ]);
        updateLastSavedTime();
      } catch (error) {
        showToast('error', t('editor.canvas.add_initiative_error'));
        await queryClient.invalidateQueries([getAllRoadmapNodes.name, roadmapId]);
      }
      setIsInitiativeImportRunning(false);
    },
    [
      createNodesForDescendants,
      generateUniqueIntTemporaryId,
      importNewRoadmapInitiativeNodeMutation,
      onCSVImportedInitiativeDrop,
      queryClient,
      roadmapId,
      selectedRoadmapId,
      setFilteredNodes,
      setIsInitiativeImportRunning,
      setSelectedNodeId,
      showToast,
      t,
      temporaryCreatedIds,
      updateLastSavedTime,
    ]
  );

  return {
    onImportInitiativeDrop,
  };
};
